import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle, ArrowLeft, Image as ImageIcon, AlertTriangle, FileText, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function AdminImportEvents() {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setResult(null);
  };

  const upload = async () => {
    if (!file) { toast.error('Seleziona un file'); return; }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/admin/import/events', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // 5min for large files
      });
      setResult(r.data);
      if (r.data.failed > 0) {
        toast.warning(`${r.data.imported} importati, ${r.data.failed} errori`);
      } else {
        toast.success(`${r.data.imported} eventi importati con successo!`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore di import');
    } finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/import/events/template.csv`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const dl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dl; a.download = 'template-eventi.csv'; a.click();
      window.URL.revokeObjectURL(dl);
      toast.success('Template scaricato');
    } catch (e) { toast.error('Errore download'); }
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <AdminLayout title="Importazione Massiva Eventi">
      <div className="flex items-center gap-2 mb-4">
        <Link to="/admin/eventi"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1"/>Eventi</Button></Link>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-4">
          {/* Upload Section */}
          <div className="rounded-xl border border-[color:var(--border-c)] bg-[color:var(--surface)] p-5">
            <h2 className="font-heading text-2xl text-[color:var(--gold)] mb-1 flex items-center gap-2"><FileSpreadsheet className="w-5 h-5"/>Carica CSV o Excel</h2>
            <p className="text-sm text-[color:var(--text-muted)] mb-4">Trascina o seleziona un file. Le immagini verranno scaricate automaticamente dagli URL e salvate nel database (gli URL di Instagram/Meta vengono "congelati" così non scadono).</p>

            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) { setFile(f); setResult(null); } }}
              className="relative rounded-xl border-2 border-dashed border-[color:var(--border-c)] hover:border-[color:var(--gold)] bg-[color:var(--surface-2)] p-8 cursor-pointer transition-colors text-center"
              data-testid="import-dropzone"
            >
              <Upload className="w-10 h-10 mx-auto text-[color:var(--gold)] opacity-70 mb-3" />
              {file ? (
                <div>
                  <p className="font-medium text-[color:var(--text)] mb-1">{file.name}</p>
                  <p className="text-xs text-[color:var(--text-muted)]">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              ) : (
                <div>
                  <p className="text-[color:var(--text)] mb-1">Clicca o trascina qui un file <b>.csv</b> o <b>.xlsx</b></p>
                  <p className="text-xs text-[color:var(--text-muted)]">Le righe con errori non bloccheranno l'import delle altre</p>
                </div>
              )}
              <input ref={inputRef} type="file" accept=".csv,.xlsx" onChange={onPick} className="hidden" data-testid="import-file-input"/>
            </div>

            <div className="flex gap-2 mt-4 flex-wrap">
              <Button data-testid="import-submit" onClick={upload} disabled={!file || busy} className="bg-[color:var(--gold-strong)] hover:bg-[#C6A21A] text-black font-semibold flex-1"><Upload className="w-4 h-4 mr-2"/>{busy ? 'Import in corso (può richiedere qualche minuto)…' : 'Avvia importazione'}</Button>
              {file && <Button variant="ghost" onClick={reset} disabled={busy}>Cambia file</Button>}
            </div>
          </div>

          {/* Results */}
          {result && (
            <div className="rounded-xl border border-[color:var(--border-c)] bg-[color:var(--surface)] p-5" data-testid="import-results">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h3 className="font-heading text-xl">Risultato Importazione</h3>
                <div className="flex gap-2">
                  <Badge className="bg-emerald-700/30 text-emerald-300">{result.imported} importati</Badge>
                  {result.failed > 0 && <Badge className="bg-red-700/30 text-red-300">{result.failed} errori</Badge>}
                  <Badge variant="outline">Totale {result.total}</Badge>
                </div>
              </div>
              <div className="divide-y divide-[color:var(--border-c)] max-h-[500px] overflow-y-auto">
                {result.results.map((r) => (
                  <div key={r.row} className="py-3 flex items-start gap-3" data-testid={`import-row-${r.row}`}>
                    {r.ok ? <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5"/> : <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5"/>}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-[color:var(--text-subtle)]">Riga {r.row}</span>
                        <span className="font-medium">{r.titolo || '(senza titolo)'}</span>
                        {r.ok && <Badge variant="outline" className={r.stato === 'Pubblicato' ? 'border-emerald-500/40 text-emerald-300' : 'border-amber-500/40 text-amber-300'}>{r.stato}</Badge>}
                        {r.ok && r.image_ok && <span className="inline-flex items-center gap-1 text-xs text-emerald-300"><ImageIcon className="w-3 h-3"/>img OK</span>}
                        {r.ok && r.slug && <a href={`/admin/eventi/${r.id}/modifica`} target="_blank" rel="noreferrer" className="text-xs text-[color:var(--gold)] inline-flex items-center gap-1 hover:underline"><ExternalLink className="w-3 h-3"/>modifica</a>}
                      </div>
                      {!r.ok && <p className="text-sm text-red-300 mt-1">{r.error}</p>}
                      {r.warnings?.map((w, i) => (
                        <p key={i} className="text-xs text-amber-300 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3"/>{w}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-[color:var(--border-c)] flex justify-end gap-2">
                <Button variant="ghost" onClick={reset}>Nuovo import</Button>
                <Link to="/admin/eventi"><Button className="bg-[color:var(--gold-strong)] hover:bg-[#C6A21A] text-black">Vai agli Eventi</Button></Link>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar Info */}
        <div className="space-y-4">
          <div className="rounded-xl border border-[color:var(--border-c)] bg-[color:var(--surface)] p-5">
            <h3 className="font-heading text-lg text-[color:var(--gold)] mb-3 flex items-center gap-2"><Download className="w-4 h-4"/>Template CSV</h3>
            <p className="text-sm text-[color:var(--text-muted)] mb-3">Scarica il template di esempio con tutte le colonne supportate.</p>
            <Button data-testid="import-template-download" onClick={downloadTemplate} variant="outline" className="w-full border-[color:var(--gold)] text-[color:var(--gold)]"><Download className="w-4 h-4 mr-2"/>Scarica template</Button>
          </div>

          <div className="rounded-xl border border-[color:var(--border-c)] bg-[color:var(--surface)] p-5">
            <h3 className="font-heading text-lg text-[color:var(--gold)] mb-3 flex items-center gap-2"><FileText className="w-4 h-4"/>Struttura colonne</h3>
            <ul className="text-xs space-y-2">
              {[
                { col: 'Titolo', req: true, info: 'Testo' },
                { col: 'Locale', req: true, info: 'Auto-creato se non esiste' },
                { col: 'Zona', req: true, info: 'Testaccio, EUR, Ostia…' },
                { col: 'Data', req: true, info: 'GG/MM/AAAA' },
                { col: 'Descrizione', req: false, info: 'Testo lungo' },
                { col: 'Link Acquisto', req: false, info: 'URL biglietto' },
                { col: 'Link Immagine', req: false, info: 'URL — scaricato in DB' },
                { col: 'Stato', req: false, info: 'Pubblicato / Bozza' },
                { col: 'Orario', req: false, info: 'default 22:00' },
                { col: 'Tavolo Prive', req: false, info: 'Si / No (default No)' },
                { col: 'Link Tavolo', req: false, info: 'URL prenotazione' },
                { col: 'In Evidenza', req: false, info: 'Si / No (default No)' },
              ].map((f) => (
                <li key={f.col} className="flex items-start gap-2">
                  {f.req ? <span className="text-red-400 mt-0.5">*</span> : <span className="text-[color:var(--text-subtle)] mt-0.5">·</span>}
                  <div>
                    <span className="text-[color:var(--text)] font-medium">{f.col}</span>
                    <span className="text-[color:var(--text-subtle)]"> — {f.info}</span>
                  </div>
                </li>
              ))}
            </ul>
            <p className="text-[10px] text-[color:var(--text-subtle)] mt-3"><span className="text-red-400">*</span> = obbligatorio</p>
          </div>

          <div className="rounded-xl border border-amber-700/40 bg-amber-950/20 p-4">
            <h4 className="text-sm font-semibold text-amber-300 mb-1 flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5"/>Immagini Instagram</h4>
            <p className="text-xs text-[color:var(--text-muted)]">Gli URL di Instagram/Meta scadono dopo 24-48h. Il sistema scarica subito ogni immagine e la salva nel database, così non si perdono mai. Se un'immagine fallisce, l'evento viene comunque creato (con una warning).</p>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
